import axios, { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import {
  Context,
  createContext,
  FC,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer
} from 'react';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import LicenseContext from 'src/contexts/LicenseContext';
import {
  Action,
  ContextEntity,
  ContextValueEntity, ErrorValues,
  DuplicateDiffAction,
  InitialAct, Mapping, MappingRule,
  SetAttributesForImport,
  SetPlaceSaveCondition,
  SetCsvColumns,
  SetErrorValues, SetFile, SetImportId,
  SetIsLoading,
  SetMappingRules,
  SetMappings, SetOnlyDatetimeAndBlankCsvColumns, SetOnlyDatetimeCsvColumns,
  SetSampleValues,
  SetSelectedFileAct,
  SetSelectedKeysAct,
  SetSelectedRuleAct, SetSelectedRuleId,
  SetStepAct,
  SetTotal,
  StepNumber, Values,
  SetUsePlaceComplement,
  SetPrioritizePlaces,
  SetSkipMapping,
  SetShipperId,
  SetShipperName,
  SetLoadingPlaceId,
  SetUnloadingPlaceId,
  SetWeightFactor,
  SetVolumeFactor,
  SetDuplicateDiffAction,
} from 'src/entities/import';
import { buildDefaultMappings } from 'src/pages/ImportPresenter/defaultMappings';

type SerializedImportOrderCache = {
  id: string;
  sample_values: Values;
  error_values: ErrorValues;
  attributes_for_import: {en: string; ja: string, required: boolean}[];
  csv_columns: string[];
  mappings: Mapping;
  total: number;
  only_datetime_csv_columns: string[];
  only_datetime_and_blank_csv_columns: string[];
}

const initialState: ContextEntity = {
  currentStep: 1,
  selectedRule: 0,
  selectedFile: null,
  selectedKeys: [],
  importId: '',
  mappingRules: [],
  sampleValues: [],
  attributesForImport: [],
  mappings: {},
  csvColumns: [],
  errorValues: [],
  isLoading: false,
  file: null,
  selectedRuleId: 0,
  placeSaveCondition: 'unloadingPlace',
  total: 0,
  only_datetime_csv_columns: [],
  only_datetime_and_blank_csv_columns: [],
  usePlaceComplement: true,
  prioritizePlaces: true,
  skipMapping: false,
  shipperId: undefined,
  shipperName: undefined,
  loadingPlaceId: undefined,
  unloadingPlaceId: undefined,
  weightFactor: undefined,
  volumeFactor: undefined,
  duplicateDiffAction: 'overwrite'
};

const handlers: Record<
  string,
  (state: ContextEntity, action: Action) => ContextEntity
> = {
  INITIALIZE: (state: ContextEntity, { data }: InitialAct): ContextEntity => ({
    ...data
  }),
  SETSTEP: (state: ContextEntity, { next }: SetStepAct): ContextEntity => ({
    ...state,
    currentStep: next
  }),
  SETSELECTEDRULE: (
    state: ContextEntity,
    { ruleId }: SetSelectedRuleAct
  ): ContextEntity => ({
    ...state,
    selectedRule: ruleId
  }),
  SETSELECTEDFILE: (
    state: ContextEntity,
    { file }: SetSelectedFileAct
  ): ContextEntity => ({
    ...state,
    selectedFile: file
  }),
  SETSELECTEDKEYS: (
    state: ContextEntity,
    { keys }: SetSelectedKeysAct
  ): ContextEntity => ({
    ...state,
    selectedKeys: keys
  }),
  SET_IMPORT_ID: (
    state: ContextEntity,
    { id }: SetImportId
  ): ContextEntity => ({
    ...state,
    importId: id
  }),
  SET_MAPPING_RULES: (
    state: ContextEntity,
    { values }: SetMappingRules
  ): ContextEntity => ({
    ...state,
    mappingRules: values
  }),
  SET_SAMPLE_VALUES: (
    state: ContextEntity,
    { values }: SetSampleValues
  ): ContextEntity => ({
    ...state,
    sampleValues: values
  }),
  SET_ERROR_VALUES: (
    state: ContextEntity,
    { values }: SetErrorValues
  ): ContextEntity => ({
    ...state,
    errorValues: values
  }),
  SET_ATTRIBUTES_FOR_IMPORT: (
    state: ContextEntity,
    { values }: SetAttributesForImport
  ): ContextEntity => ({
    ...state,
    attributesForImport: values
  }),
  SET_CSV_COLUMNS: (
    state: ContextEntity,
    { values }: SetCsvColumns
  ): ContextEntity => ({
    ...state,
    csvColumns: values
  }),
  SET_MAPPINGS: (
    state: ContextEntity,
    { mappings }: SetMappings
  ): ContextEntity => ({
    ...state,
    mappings
  }),
  SET_IS_LOADING: (
    state: ContextEntity,
    { value }: SetIsLoading
  ): ContextEntity => ({
    ...state,
    isLoading: value
  }),
  SET_FILE: (
    state: ContextEntity,
    { value }: SetFile
  ): ContextEntity => ({
    ...state,
    file: value
  }),
  SET_SELECTED_RULE_ID: (
    state: ContextEntity,
    { value }: SetSelectedRuleId
  ): ContextEntity => ({
    ...state,
    selectedRuleId: value
  }),
  SET_PLACE_SAVE_CONDITION: (
    state: ContextEntity,
    { value }: SetPlaceSaveCondition
  ): ContextEntity => ({
    ...state,
    placeSaveCondition: value
  }),
  SET_TOTAL: (
    state: ContextEntity,
    { value }: SetTotal
  ): ContextEntity => ({
    ...state,
    total: value
  }),
  SET_ONLY_DATETIME_CSV_COLUMNS: (
    state: ContextEntity,
    { values }: SetOnlyDatetimeCsvColumns
  ): ContextEntity => ({
    ...state,
    only_datetime_csv_columns: values
  }),
  SET_ONLY_DATETIME_AND_BLANK_CSV_COLUMNS: (
    state: ContextEntity,
    { values }: SetOnlyDatetimeAndBlankCsvColumns
  ): ContextEntity => ({
    ...state,
    only_datetime_and_blank_csv_columns: values
  }),
  SET_USE_PLACE_COMPLEMENT: (
    state: ContextEntity,
    { value }: SetUsePlaceComplement
  ): ContextEntity => ({
    ...state,
    usePlaceComplement: value
  }),
  SET_PRIORITIZE_PLACES: (
    state: ContextEntity,
    { value }: SetPrioritizePlaces
  ): ContextEntity => ({
    ...state,
    prioritizePlaces: value
  }),
  SET_SKIP_MAPPING: (
    state: ContextEntity,
    { value }: SetSkipMapping
  ): ContextEntity => ({
    ...state,
    skipMapping: value
  }),
  SET_SHIPPER_ID: (
    state: ContextEntity,
    { value } : SetShipperId
  ): ContextEntity => ({
    ...state,
    shipperId: value
  }),
  SET_SHIPPER_NAME: (
    state: ContextEntity,
    { value } : SetShipperName
  ): ContextEntity => ({
    ...state,
    shipperName: value
  }),
  SET_LOADING_PLACE_ID: (
    state: ContextEntity,
    { value }: SetLoadingPlaceId
  ): ContextEntity => ({
    ...state,
    loadingPlaceId: value
  }),
  SET_UNLOADING_PLACE_ID: (
    state: ContextEntity,
    { value }: SetUnloadingPlaceId
  ): ContextEntity => ({
    ...state,
    unloadingPlaceId: value
  }),
  SET_WEIGHT_FACTOR: (
    state: ContextEntity,
    { value }: SetWeightFactor
  ): ContextEntity => ({
    ...state,
    weightFactor: value
  }),
  SET_VOLUME_FACTOR: (
    state: ContextEntity,
    { value }: SetVolumeFactor
  ): ContextEntity => ({
    ...state,
    volumeFactor: value
  }),
  SET_DUPLICATE_DIFF_ACTION: (
    state: ContextEntity,
    { value }: SetDuplicateDiffAction
  ): ContextEntity => ({
    ...state,
    duplicateDiffAction: value
  }),
};

const reducer = (state: ContextEntity, action: Action): ContextEntity => (handlers[action.type] ? handlers[action.type](state, action) : state);

const ImportContext: Context<ContextValueEntity> = createContext<ContextValueEntity>({
  ...initialState,
  setStep: (next: StepNumber) => false,
  setSelectedRule: (ruleId: number) => false,
  setSelectedFile: (file: File) => false,
  setSelectedKeys: (keys: string[]) => false,
  postImports: () => Promise.resolve(),
  patchImportCacheMappings: () => Promise.resolve(),
  setImportId: (id: string) => false,
  setMappingRules: (values: { id: number; name: string; }[]) => false,
  setSampleValues: (values: Values) => false,
  setErrorValues: (values: ErrorValues) => false,
  setAttributesForImport: (values: {en: string; ja: string, required: boolean}[]) => false,
  setCsvColumns: (values: string[]) => false,
  setMappings: (mappings: Mapping) => false,
  setIsLoading: (value: boolean) => false,
  setFile: (value: File) => false,
  setSelectedRuleId: (value: number) => false,
  setPlaceSaveCondition: (value: string) => false,
  isDefaultMappings: () => false,
  setUsePlaceComplement: (value: boolean) => false,
  setPrioritizePlaces: (value: boolean) => false,
  setSkipMapping: (value: boolean) => false,
  setShipperId: (value: number) => false,
  setShipperName: (value: string) => false,
  setLoadingPlaceId: (value: number) => false,
  setUnloadingPlaceId: (value: number) => false,
  setWeightFactor: (value: number) => false,
  setVolumeFactor: (value: number) => false,
  setDuplicateDiffAction: (value: DuplicateDiffAction) => false,
  getMappingRule: () => undefined,
});

export const defaultMappingsId = -1;

export const ImportProvider: FC<{ children: ReactNode }> = memo((props) => {
  const { children } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [state, dispatch] = useReducer(reducer, initialState);

  const licenseContext = useContext(LicenseContext);
  const customInputFields = useMemo(
    () => licenseContext?.config?.custom_input_fields || [],
    [licenseContext?.config]
  );
  const defaultMappings = useMemo(
    () => buildDefaultMappings(customInputFields),
    [customInputFields]
  );

  useEffect(() => {
    dispatch({
      type: 'INITIALIZE',
      data: initialState
    });
  }, []);

  const setStep = (next: StepNumber) => {
    dispatch({
      type: 'SETSTEP',
      next
    });
    return true;
  };

  const setSelectedRule = (ruleId: number) => {
    dispatch({
      type: 'SETSELECTEDRULE',
      ruleId
    });
    return true;
  };

  const setSelectedFile = (file: File) => {
    dispatch({
      type: 'SETSELECTEDFILE',
      file
    });
    return true;
  };

  const setSelectedKeys = (keys: string[]) => {
    dispatch({
      type: 'SETSELECTEDKEYS',
      keys
    });
    return true;
  };

  const setImportId = (id: string) => {
    dispatch({
      type: 'SET_IMPORT_ID',
      id
    });
    return true;
  };

  const setMappingRules = (values: MappingRule[]) => {
    dispatch({
      type: 'SET_MAPPING_RULES',
      values
    });
    return true;
  };

  const setSampleValues = (values: Values) => {
    dispatch({
      type: 'SET_SAMPLE_VALUES',
      values
    });
    return true;
  };

  const setErrorValues = (values: ErrorValues) => {
    dispatch({
      type: 'SET_ERROR_VALUES',
      values
    });
    return true;
  };

  const setAttributesForImport = (values: {en: string; ja: string, required: boolean}[]) => {
    dispatch({
      type: 'SET_ATTRIBUTES_FOR_IMPORT',
      values
    });
    return true;
  };

  const setCsvColumns = (values: string[]) => {
    dispatch({
      type: 'SET_CSV_COLUMNS',
      values
    });
    return true;
  };

  const setMappings = (mappings: Mapping) => {
    dispatch({
      type: 'SET_MAPPINGS',
      mappings
    });
    return true;
  };

  const setIsLoading = (value: boolean) => {
    dispatch({
      type: 'SET_IS_LOADING',
      value
    });
    return true;
  };

  const setFile = (value: File) => {
    dispatch({
      type: 'SET_FILE',
      value
    });
    return true;
  };

  const setSelectedRuleId = (value: number) => {
    dispatch({
      type: 'SET_SELECTED_RULE_ID',
      value
    });
    return true;
  };

  const setPlaceSaveCondition = (value: string) => {
    dispatch({
      type: 'SET_PLACE_SAVE_CONDITION',
      value
    });
    return true;
  };

  const setTotal = (value: number) => {
    dispatch({
      type: 'SET_TOTAL',
      value
    });
    return true;
  };

  const setOnlyDatetimeCsvColumns = (values: string[]) => {
    dispatch({
      type: 'SET_ONLY_DATETIME_CSV_COLUMNS',
      values
    });

    return true;
  };

  const setOnlyDatetimeAndBlankCsvColumns = (values: string[]) => {
    dispatch({
      type: 'SET_ONLY_DATETIME_AND_BLANK_CSV_COLUMNS',
      values
    });

    return true;
  };

  const setUsePlaceComplement = (value: boolean) => {
    dispatch({
      type: 'SET_USE_PLACE_COMPLEMENT',
      value
    });

    return true;
  };

  const setPrioritizePlaces = (value: boolean) => {
    dispatch({
      type: 'SET_PRIORITIZE_PLACES',
      value,
    });

    return true;
  };

  const setSkipMapping = (value: boolean) => {
    dispatch({
      type: 'SET_SKIP_MAPPING',
      value,
    });

    return true;
  };

  const setShipperId = (value: number) => {
    dispatch({
      type: 'SET_SHIPPER_ID',
      value,
    });
    return true;
  };

  const setShipperName = (value: string) => {
    dispatch({
      type: 'SET_SHIPPER_NAME',
      value,
    });
    return true;
  };

  const setLoadingPlaceId = (value: number) => {
    dispatch({
      type: 'SET_LOADING_PLACE_ID',
      value,
    });
    return true;
  };

  const setUnloadingPlaceId = (value: number) => {
    dispatch({
      type: 'SET_UNLOADING_PLACE_ID',
      value,
    });
    return true;
  };

  const setWeightFactor = (value: number) => {
    dispatch({
      type: 'SET_WEIGHT_FACTOR',
      value,
    });
    return true;
  };

  const setVolumeFactor = (value: number) => {
    dispatch({
      type: 'SET_VOLUME_FACTOR',
      value,
    });
    return true;
  };

  const setDuplicateDiffAction = (value: DuplicateDiffAction) => {
    dispatch({
      type: 'SET_DUPLICATE_DIFF_ACTION',
      value,
    });
    return true;
  };

  const isDefaultMappings = () => {
    if (state.selectedRuleId !== defaultMappingsId) return false;

    return Object.keys(defaultMappings).every((key) => defaultMappings[key] === state.mappings[key]);
  };

  const handleResponse = (entity: SerializedImportOrderCache) => {
    setImportId(entity.id);
    setCsvColumns(entity.csv_columns);
    setErrorValues(entity.error_values);
    setSampleValues(entity.sample_values);
    setAttributesForImport(entity.attributes_for_import);
    if (state.selectedRuleId !== defaultMappingsId) setMappings(entity.mappings);
    setTotal(entity.total);
    setOnlyDatetimeCsvColumns(entity.only_datetime_csv_columns);
    setOnlyDatetimeAndBlankCsvColumns(entity.only_datetime_and_blank_csv_columns);
  };

  // TODO: ReactQueryにしたいです
  const getMappings = () => {
    setIsLoading(true);

    const requestPath = '/api/v3/orders/mappings';

    axios.get(requestPath)
      .then((response: AxiosResponse<MappingRule[]>) => {
        setMappingRules(response.data);
      })
      .catch((e) => {
        enqueueSnackbar(fiveZeroZeroErrorMessage);
        throw e;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const postImports: () => Promise<void> = async () => {
    if (!state.file) return;

    const requestPath = '/api/v3/orders/imports';
    const requestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };

    const data = new FormData();
    data.append('file', state.file);
    if (state.selectedRuleId !== 0) {
      data.append('mapping_id', `${state.selectedRuleId}`);
      data.append('skip_mapping', state.skipMapping.toString());
    }
    data.append('place_save_condition', state.placeSaveCondition);
    data.append('use_place_complement', state.usePlaceComplement.toString());
    if (state.usePlaceComplement && state.prioritizePlaces) {
      data.append('prioritize_places', state.prioritizePlaces.toString());
    }

    const response: AxiosResponse<SerializedImportOrderCache> = await axios.post(
      requestPath,
      data,
      requestConfig
    );

    handleResponse(response.data);
  };

  const getImportCache = () => {
    if (!state.importId) return;

    const requestPath = `/api/v3/orders/imports/${state.importId}`;

    setIsLoading(true);

    axios.get(requestPath)
      .then((response: AxiosResponse<SerializedImportOrderCache>) => {
        handleResponse(response.data);
      })
      .catch((e) => {
        enqueueSnackbar(fiveZeroZeroErrorMessage);
        throw e;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getMappingRule = (): MappingRule | undefined => ({
    id: state.selectedRuleId,
    import_id: state.importId,
    name: state.mappingRules.find((rule) => rule.id === state.selectedRuleId)?.name || '',
    mapping: state.mappings,
    shipperId: state.shipperId,
    shipperName: state.shipperName,
    loadingPlaceId: state.loadingPlaceId,
    unloadingPlaceId: state.unloadingPlaceId,
    weightFactor: state.weightFactor,
    volumeFactor: state.volumeFactor,
    duplicateDiffAction: state.duplicateDiffAction,
  });

  const patchImportCacheMappings: () => Promise<void> = async () => {
    const existMappings = () => !!Object
      .values(state.mappings)
      .filter((maybe) => !!maybe)
      .length;

    if (state.mappings && !existMappings()) return;

    const requestPath = `/api/v3/orders/imports/${state.importId}/mappings`;
    const response: AxiosResponse<SerializedImportOrderCache> = await axios.patch(
      requestPath,
      { mappings: state.mappings }
    );

    handleResponse(response.data);
  };

  useEffect(() => {
    getMappings();
  }, []);

  useEffect(() => {
    if (state.skipMapping) return;

    getImportCache();
  }, [state.importId, state.skipMapping]);

  useEffect(() => {
    if (state.selectedRuleId === defaultMappingsId) {
      dispatch({
        type: 'SET_MAPPINGS',
        mappings: defaultMappings
      });
    }
  }, [state.selectedRuleId, defaultMappings]);

  const value = useMemo(
    () => ({
      ...state,
      setStep,
      setSelectedRule,
      setSelectedFile,
      setSelectedKeys,
      postImports,
      patchImportCacheMappings,
      setImportId,
      setMappingRules,
      setSampleValues,
      setErrorValues,
      setAttributesForImport,
      setMappings,
      setCsvColumns,
      setIsLoading,
      setFile,
      setSelectedRuleId,
      setPlaceSaveCondition,
      isDefaultMappings,
      setUsePlaceComplement,
      setPrioritizePlaces,
      setSkipMapping,
      setShipperId,
      setShipperName,
      setLoadingPlaceId,
      setUnloadingPlaceId,
      setWeightFactor,
      setVolumeFactor,
      setDuplicateDiffAction,
      getMappingRule,
    }),
    [state]
  );

  return (
    <ImportContext.Provider value={value}>{children}</ImportContext.Provider>
  );
});

// ImportProvider.propTypes = {
//   children: PropTypes.node.isRequired
// };

export default ImportContext;
